// src/components/inventory/LiveIndicator.js
// --- UPDATE (Inventory MOW+ v2) — NEW FILE -----------------------------------------------
// Small "Live" pill: pulsing dot (green = WebSocket connected / event received, amber = polling
// only), last refresh time, last change reason in the tooltip, optional refresh button.
// --- END UPDATE ---------------------------------------------------------------------------
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { HStack, Box, Text, Tooltip, IconButton, Icon, useColorModeValue } from '@chakra-ui/react';
import { MdRefresh } from 'react-icons/md';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);
const REASONS = {
    item_created: 'item added', item_updated: 'item edited', item_deleted: 'item deleted', movement: 'stock movement', link: 'part link changed', vehicle: 'vehicle changed', vehicle_event: 'vehicle log entry',
    category_changed: 'category changed', alert_dismissed: 'alert dismissed', low_stock: 'new low-stock alert', low_stock_alert: 'new low-stock alert',
    items_imported: 'CSV import', manual_refresh: 'manual refresh', work_order_completed: 'work order completed', poll: 'scheduled refresh',
};
export const reasonLabel = (e) => (!e ? null : REASONS[e.reason] || REASONS[e.type] || String(e.reason || e.type || 'update').replace(/_/g, ' '));

function LiveIndicator({ connected, updatedAt, lastEvent, isRefreshing, onRefresh, label = 'Live' }) {
    const muted = useColorModeValue('gray.500', 'gray.400');
    const pill = useColorModeValue('whiteAlpha.900', 'whiteAlpha.100');
    const border = useColorModeValue('gray.200', 'whiteAlpha.200');
    const live = connected !== false;
    const color = connected === false ? 'orange.400' : 'green.400';
    const tip = [
        connected === true ? 'Connected to FlowSense live updates (WebSocket).' : connected === false ? 'WebSocket not connected — refreshing on a timer.' : 'Listening for FlowSense live updates; refreshes on a timer as a fallback.',
        lastEvent ? `Last change: ${reasonLabel(lastEvent)} at ${lastEvent.at.toLocaleTimeString()}` : null,
    ].filter(Boolean).join('\n');
    return (
        <HStack spacing={1.5} bg={pill} borderWidth="1px" borderColor={border} borderRadius="full" pl={2.5} pr={onRefresh ? 0.5 : 2.5} py={0.5} role="status" aria-label="Live status">
            <Tooltip hasArrow label={tip} whiteSpace="pre-line">
                <HStack spacing={1.5} cursor="help">
                    <Box position="relative" w="8px" h="8px">
                        {live && <MotionBox position="absolute" inset={0} borderRadius="full" bg={color} animate={{ scale: [1, 2.2], opacity: [0.6, 0] }} transition={{ duration: 1.6, repeat: Infinity }} />}
                        <Box position="absolute" inset={0} borderRadius="full" bg={color} />
                    </Box>
                    <Text fontSize="xs" fontWeight="700">{connected === false ? 'Auto-refresh' : label}</Text>
                    <Text fontSize="xs" color={muted}>{updatedAt ? `· ${updatedAt.toLocaleTimeString()}` : '· connecting…'}</Text>
                </HStack>
            </Tooltip>
            {onRefresh && <IconButton size="xs" variant="ghost" borderRadius="full" icon={<Icon as={MdRefresh} />} aria-label="Refresh now" onClick={onRefresh} isLoading={isRefreshing} />}
        </HStack>
    );
}
LiveIndicator.propTypes = { connected: PropTypes.bool, updatedAt: PropTypes.instanceOf(Date), lastEvent: PropTypes.object, isRefreshing: PropTypes.bool, onRefresh: PropTypes.func, label: PropTypes.string };
export default memo(LiveIndicator);
